import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';

import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import AntTime from '../utils/AntTime';
import TileView from 'devextreme-react/tile-view';

import { Template } from 'devextreme-react/core/template';
import AntConfig from '../utils/AntConfig';

import AntLocalization from '../utils/AntLocalization';
import AntCondition from '../utils/AntCondition';
import AntArray from '../utils/AntArray';
import { AntGlobal, AntSession } from '../utils/AntContext';

import AntCardViewCell from './AntCardViewCell';
import AntCardListSkeleton from './AntCardListSkeleton';
import useWidgetFirstLoad from './AntWidgetSkeleton/useWidgetFirstLoad';
import AntCardViewUtil from './AntCardViewUtil';

export default forwardRef(function (props, ref) {
    const appGlobal = AntGlobal();
    // const session = AntSession();
    const location = useLocation();
    const navigate = useNavigate();

    if (props.API && !props.API.entityDisplay) {
        if (appGlobal && appGlobal.appConfig && appGlobal.appConfig.Services) {
            let found = appGlobal.appConfig.Services.find((s) => s.name === props.API.entityName);
            if (found) props.API.init({ entityDisplay: found.display });
        }
    }

    const tableName = props.table ? props.table : props.API ? props.API.entityName : '';

    const config = useMemo(() => {
        let found = appGlobal.tableConfigs?.find((t) => t.tableName === tableName);
        if (found) return found.config;

        let cnf = new AntConfig(appGlobal, tableName);
        cnf.configFields(props.fields);

        if (appGlobal.tableConfigs instanceof Array === false) appGlobal.tableConfigs = [];

        appGlobal.tableConfigs.push({
            tableName: tableName,
            config: cnf
        });
        return cnf;
    }, []);

    const CardUtil = useMemo(() => {
        let util = new AntCardViewUtil(config, tableName);
        return util;
    }, []);

    const selectedItem = useRef();
    const tileView = useRef();
    const tileViewData = useRef();
    const baseItemWidth = useRef(!props.colCount && props.baseItemWidth ? props.baseItemWidth : 100);

    const baseItemHeight = useRef(props.baseItemHeight ? props.baseItemHeight : 100);

    const forceRender = useCallback(() => {
        setShouldRender((v) => v + 1);
    }, []);

    const [shouldRender, setShouldRender] = useState(1);

    // Khung xám CHỈ cho lượt tải đầu tiên. Các lượt tải lại giữ nguyên thẻ đang hiển thị.
    const [loaded, markLoaded] = useWidgetFirstLoad(true);

    const isRender = useMemo(() => shouldRender + (props.shouldRender ? props.shouldRender : 1), [props.shouldRender, shouldRender]);

    const reload = useCallback(() => {
        if (tileView.current && tileView.current.instance) tileView.current.instance.getDataSource().reload();
    }, []);

    const initBaseHeight = useCallback(() => {
        let height = props.baseItemHeight ? props.baseItemHeight : 0;

        let maxRowsCount = 0;
        if (tileViewData.current instanceof Array) {
            for (let i = 0; i < tileViewData.current.length; i++) {
                const item = tileViewData.current[i];
                let count = CardUtil.getVisibileRowsCount({
                    props: props,
                    item: item,
                    cardLayout: props.cardLayout
                });
                if (count > maxRowsCount) maxRowsCount = count;
            }
        }

        if (maxRowsCount) height = maxRowsCount * 30 + 30;

        if (height < 100) height = 100;

        return height;
    }, [props, props.baseItemHeight]);

    const onLoadData = useCallback(
        (data, allData) => {
            markLoaded();
            tileViewData.current = data.data;
            let height = initBaseHeight();
            if (height !== baseItemHeight.current) {
                baseItemHeight.current = height;
                forceRender();
            }
        },
        [forceRender, initBaseHeight, markLoaded]
    );

    const onItemClick = useCallback(
        (e) => {
            if (e.itemData && e.itemData.actions instanceof Array && e.itemData.actions.length > 0) {
                if (selectedItem.current && selectedItem.current.itemData && selectedItem.current.itemData.id === e.itemData.id)
                    selectedItem.current = null;
                else selectedItem.current = e;

                e.component?.repaint();
            } else if (props.onItemClick) props.onItemClick(e.itemData);
        },
        [props.onItemClick]
    );

    const onAction = useCallback(
        async (e) => {
            let result;
            if (props.onAction) result = await props.onAction(e);

            selectedItem.current = null;

            e.component?.repaint();
            return result;
        },
        [props.onAction, forceRender]
    );

    const getCellStyle = useCallback(
        (itemData) => {
            if (props.cardStyles instanceof Array)
                for (let i = 0; i < props.cardStyles.length; i++) {
                    const cellStyle = props.cardStyles[i];

                    if (cellStyle.when && typeof cellStyle.when === 'object') {
                        var isValid = AntCondition.isValid(itemData, {
                            when: cellStyle.when,
                            whenType: cellStyle.whenType
                        });

                        if (isValid) return cellStyle;
                    }
                }
            return null;
        },
        [props.cardStyles]
    );

    useImperativeHandle(ref, () => ({
        reload: reload,
        onLoadData: onLoadData
    }));

    const itemlRender = useCallback(
        (data, i) => {
            return (
                <AntCardViewCell
                    key={i}
                    API={props.API}
                    maxHeight={baseItemHeight.current ? baseItemHeight.current : 100}
                    table={props.table}
                    item={data}
                    itemIndex={i}
                    isSelected={Boolean(selectedItem.current && selectedItem.current.itemData && selectedItem.current.itemData.id === data.id)}
                    cardLayout={props.cardLayout}
                    excludesRowActions={props.excludesRowActions}
                    cardStyles={props.cardStyles}
                    cellActions={props.cellActions}
                    showDetail={props.showDetail}
                    onAction={onAction}
                />
            );
        },
        [onAction, props.table, props.API, props.showDetail, props.cardLayout, props.excludesRowActions, props.cardStyles, props.cellActions]
    );

    const colCount = props.colCount ? props.colCount : null;

    const onContentReady = useCallback(
        (e) => {
            const width = e.element.clientWidth;
            let cCount = props.colCount ? props.colCount : null;

            if (width && cCount) {
                let oldBaseWidth = e.component.option('baseItemWidth');
                let itemMargin = e.component.option('itemMargin');
                let baseWidth = parseInt((width - itemMargin * cCount) / cCount);
                if (baseWidth !== oldBaseWidth) {
                    baseItemWidth.current = baseWidth;
                    forceRender();
                }
            }
        },
        [forceRender, props.colCount]
    );

    return (
        <React.Fragment>
            {isRender && (
                <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                    {!loaded && (
                        <AntCardListSkeleton
                            cardLayout={props.cardLayout}
                            cardClasses={props.cardClasses}
                            colCount={props.colCount}
                            cellHeight={baseItemHeight.current}
                        />
                    )}
                    <TileView
                    ref={tileView}
                    {...props}
                    noDataText={''}
                    showScrollbar={'never'}
                    dataSource={props.dataSource}
                    itemTemplate={'itemlRender'}
                    width="100%"
                    height="100%"
                    itemMargin={0}
                    baseItemHeight={baseItemHeight.current ? baseItemHeight.current : 100}
                    baseItemWidth={baseItemWidth.current}
                    onContentReady={onContentReady}
                    onItemClick={onItemClick}
                    // onItemRendered={(e) => {
                    //     let style = getCellStyle(e.itemData);
                    //     if (style && style.backgroundColor) {
                    //         let ele = e.itemElement.querySelector('.dx-tile-content');
                    //         if (ele) ele.style.backgroundColor = style.backgroundColor;
                    //     }
                    // }}
                    >
                    <Template name={'itemlRender'} render={itemlRender} />
                    </TileView>
                </div>
            )}
        </React.Fragment>
    );
});
